import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Mail, Lock, LogIn, Eye, EyeOff, CheckCircle2 } from 'lucide-react'
import { loginSchema, type LoginValues } from '../../model/schemas'
import { AuthCard } from '../components/AuthCard'
import { SocialButtons } from '../components/SocialButtons'
import { useLoginMutation } from '@/modules/auth-api'

type Errors = Partial<Record<keyof LoginValues, string>>

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const loginMut = useLoginMutation()

  const [values, setValues] = useState<LoginValues>({ identifier: '', password: '', remember: true })
  const [errors, setErrors] = useState<Errors>({})
  const [serverError, setServerError] = useState<string | null>(null)
  const [showPass, setShowPass] = useState(false)
  const [success, setSuccess] = useState(false)

  const upd = <K extends keyof LoginValues>(k: K, v: LoginValues[K]) => {
    setValues((s) => ({ ...s, [k]: v }))
    setErrors((e) => ({ ...e, [k]: undefined }))
    setServerError(null)
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setServerError(null)
    const parsed = loginSchema.safeParse(values)
    if (!parsed.success) {
      const next: Errors = {}
      for (const i of parsed.error.issues) {
        const k = i.path[0] as keyof LoginValues
        if (!next[k]) next[k] = i.message
      }
      setErrors(next)
      return
    }
    try {
      await loginMut.mutateAsync({ identifier: values.identifier, password: values.password })
      setSuccess(true)
      const from = (location.state as { from?: string } | null)?.from ?? '/'
      setTimeout(() => navigate(from, { replace: true }), 900)
    } catch (err: any) {
      setServerError(err?.message ?? 'Đăng nhập thất bại')
    }
  }

  const inputCls = (err?: string) => [
    'w-full h-11 rounded-md border bg-surface pl-10 pr-3.5 text-sm outline-none transition',
    err ? 'border-danger focus:border-danger' : 'border-border focus:border-brand-500',
  ].join(' ')

  return (
    <AuthCard
      title="Đăng nhập"
      subtitle="Chào mừng bạn trở lại — đăng nhập để tiếp tục mua sắm"
      footer={<>Chưa có tài khoản? <Link to="/dang-ky" className="text-brand-600 font-semibold hover:underline">Đăng ký ngay</Link></>}
    >
      {success ? (
        <div className="py-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-accent-500/15 text-accent-600 flex items-center justify-center mb-3">
            <CheckCircle2 className="w-9 h-9" />
          </div>
          <div className="font-semibold text-text text-lg">Đăng nhập thành công!</div>
          <div className="text-sm text-muted mt-1">Đang chuyển về trang chủ...</div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {serverError && (
            <div className="rounded-md bg-danger/10 border border-danger/30 text-danger px-4 py-2.5 text-sm">
              {serverError}
            </div>
          )}

          <label className="block">
            <div className="text-sm font-medium text-text mb-1.5">Username / Email / Số điện thoại</div>
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
              <input
                value={values.identifier}
                onChange={(e) => upd('identifier', e.target.value)}
                placeholder="username, email hoặc 0912345678"
                autoComplete="username"
                className={inputCls(errors.identifier)}
              />
            </div>
            {errors.identifier && <div className="text-xs text-danger mt-1">{errors.identifier}</div>}
          </label>

          <label className="block">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-medium text-text">Mật khẩu</span>
              <Link to="/quen-mat-khau" className="text-xs text-brand-600 hover:underline">Quên mật khẩu?</Link>
            </div>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
              <input
                type={showPass ? 'text' : 'password'}
                value={values.password}
                onChange={(e) => upd('password', e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                className={inputCls(errors.password) + ' pr-10'}
              />
              <button
                type="button"
                onClick={() => setShowPass(!showPass)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-text"
                aria-label={showPass ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
              >
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.password && <div className="text-xs text-danger mt-1">{errors.password}</div>}
          </label>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={values.remember ?? false}
              onChange={(e) => upd('remember', e.target.checked)}
              className="w-4 h-4 accent-brand-600 rounded"
            />
            <span className="text-sm text-text">Ghi nhớ đăng nhập</span>
          </label>

          <button
            type="submit"
            disabled={loginMut.isPending}
            className="w-full h-12 rounded-md bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white font-semibold text-sm inline-flex items-center justify-center gap-2 transition"
          >
            <LogIn className="w-4 h-4" />
            {loginMut.isPending ? 'Đang đăng nhập...' : 'Đăng nhập'}
          </button>

          <SocialButtons />
        </form>
      )}
    </AuthCard>
  )
}
